import { createFileRoute, Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { HERO as HERO_PATH } from "@/lib/villa-content";

const CANONICAL = "https://www.casamipajobe.com/guide";
const HERO = `https://www.casamipajobe.com${HERO_PATH}`;

export const Route = createFileRoute("/guide")({
  head: () => ({
    meta: [
      { title: "Things to do in La Nucia & Costa Blanca, Local Guide" },
      { name: "robots", content: "index,follow,max-image-preview:large,max-snippet:-1" },
      {
        name: "description",
        content:
          "Local guide to La Nucia & Costa Blanca: beaches, markets, hikes in Serra Gelada, day trips to Guadalest, Calpe & Alicante, food and family ideas.",
      },
      {
        property: "og:title",
        content: "Things to do in La Nucia & Costa Blanca",
      },
      {
        property: "og:description",
        content:
          "Beaches, markets, hikes, day trips and family activities around La Nucia, written by villa hosts on the Costa Blanca.",
      },
      { property: "og:url", content: CANONICAL },
      { property: "og:type", content: "article" },
      { property: "og:image", content: HERO },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: HERO },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TravelGuide",
          name: "Things to do in La Nucia & Costa Blanca",
          url: CANONICAL,
          image: HERO,
          inLanguage: "en",
          about: {
            "@type": "Place",
            name: "La Nucia",
            address: {
              "@type": "PostalAddress",
              addressLocality: "La Nucia",
              addressRegion: "Alicante",
              addressCountry: "ES",
            },
          },
            isPartOf: { "@id": "https://www.casamipajobe.com/#website" },
        }),
      },
    ],
  }),
  component: GuidePage,
});

type Section = {
  id: string;
  title: string;
  body: string;
  items?: { name: string; detail: string; url?: string }[];
};

const SECTIONS: Section[] = [
  {
    id: "la-nucia",
    title: "La Nucia, our home village",
    body:
      "La Nucia is a quiet hillside town just inland from Benidorm. It keeps a relaxed Spanish feel, small plazas, family bakeries, and a busy Sunday market, while still being a 10-minute drive from the coast. Most guests use it as a calm base and head out to the beaches, mountains or day-trip towns from here.",
    items: [
      {
        name: "Mercadillo de La Nucia (Sunday market)",
        detail:
          "One of the largest weekly markets on the Costa Blanca. Local produce, olives, cheese, leather and clothes. Get there before 11:00 to avoid the heat and crowds.",
        url: "https://www.lanucia.es/",
      },
      {
        name: "Plaça Major & old town",
        detail:
          "The restored old square has cafés and tapas bars under the church. Nice for a slow evening drink after a beach day.",
        url: "https://www.lanucia.es/turismo",
      },
      {
        name: "Captivador & Favara reservoirs",
        detail:
          "Short walks and runs around the local reservoirs with views over the Sierra Helada. Quiet at sunrise.",
        url: "https://es.wikiloc.com/wikiloc/find.do?q=captivador+la+nucia",
      },
    ],
  },
  {
    id: "beaches",
    title: "Beaches near La Nucia",
    body:
      "All the main Costa Blanca beaches are within a 15-35 minute drive. The closest are in Benidorm and Altea; quieter coves sit just north towards Calpe.",
    items: [
      {
        name: "Playa de Levante, Benidorm",
        detail:
          "The famous long Blue-Flag beach. Soft sand, lifeguards, plenty of chiringuitos. Best for families and easy access. ~15 min by car.",
        url: "https://www.visitbenidorm.es/ver/148/playa-levante.html",
      },
      {
        name: "Playa de Poniente, Benidorm",
        detail:
          "Quieter than Levante, with the modern palm-lined promenade designed by Carlos Ferrater. Great for an evening walk.",
        url: "https://www.visitbenidorm.es/ver/147/playa-de-poniente-benidorm--playas-y-calas-.html",
      },
      {
        name: "Cala Mascarat & Cala Advocat (Altea/Calpe)",
        detail:
          "Small pebble coves with clear water, better for snorkelling than sandcastles. Bring water shoes.",
        url: "https://www.calpe.es/",
      },
      {
        name: "Playa de l'Albir",
        detail:
          "Pebble beach backed by a flat promenade, framed by the Serra Gelada natural park. Walk on to the Faro del Albir lighthouse (see below).",
        url: "https://welcomelalfas.com/en/",
      },
    ],
  },
  {
    id: "hiking",
    title: "Hiking & nature",
    body:
      "The Serra Gelada natural park and the inland mountains around Guadalest offer dozens of marked trails, from a flat 45-minute lighthouse walk to a full-day summit.",
    items: [
      {
        name: "Faro del Albir lighthouse walk",
        detail:
          "2.5 km each way on a paved coastal track inside the natural park. Easy with kids, stunning cliff views. Free, open daily.",
        url: "https://parquesnaturales.gva.es/es/web/pn-serra-gelada",
      },
      {
        name: "Serra Gelada ridge",
        detail:
          "Tougher 8-12 km loop along the ridge between Albir and Benidorm. Go early, almost no shade. Sunrise from the cross is unforgettable.",
        url: "https://parquesnaturales.gva.es/es/web/pn-serra-gelada",
      },
      {
        name: "Ponoig (the 'Sleeping Lion')",
        detail:
          "Iconic limestone peak visible from the villa. The full ascent is a scramble (experienced hikers only); the lower trails are family-friendly.",
        url: "https://es.wikiloc.com/wikiloc/find.do?q=ponoig",
      },
    ],
  },
  {
    id: "day-trips",
    title: "Day trips from La Nucia",
    body:
      "Almost everything on this list is within an hour's drive. Pick one or two per week, Costa Blanca holidays are better when you don't try to do it all.",
    items: [
      {
        name: "Guadalest (30 min)",
        detail:
          "Tiny castle village dramatically perched above a turquoise reservoir. Go mid-week before lunch; park below and walk up.",
        url: "https://guadalest.es/",
      },
      {
        name: "Altea old town (15 min)",
        detail:
          "Whitewashed houses, the blue-domed church, and dozens of small galleries and restaurants. Especially nice at sunset.",
        url: "https://www.visitaltea.es/",
      },
      {
        name: "Calpe & the Peñón de Ifach (25 min)",
        detail:
          "Sheer rock rising 332 m straight out of the sea. The hike to the top takes about 2 hours; book a slot in summer.",
        url: "https://parquesnaturales.gva.es/es/web/pn-penyal-d-ifac",
      },
      {
        name: "Alicante city (45 min)",
        detail:
          "Santa Bárbara castle, the Explanada promenade, and great tapas in the El Barrio quarter. Easy by car or by tram from Benidorm.",
        url: "https://www.alicanteturismo.com/",
      },
      {
        name: "Algar Waterfalls (20 min)",
        detail:
          "Cold, clear pools in a short river canyon. Perfect on a 35 °C day. Small entrance fee, parking on site.",
        url: "https://lasfuentesdelalgar.com/",
      },
      {
        name: "Terra Mítica & Aqualandia (15 min)",
        detail:
          "The big Benidorm theme parks. Aqualandia is the better bet on a hot day; Terra Mítica is a full-day historical theme park.",
        url: "https://www.terramiticapark.com/",
      },
    ],
  },
  {
    id: "food",
    title: "Food & drink",
    body:
      "Costa Blanca cooking is rice-led: the original arroz a banda, arroz del senyoret and traditional Valencian paella all come from this region. A few favourites within easy reach:",
    items: [
      {
        name: "Sunday paella in Altea or Albir",
        detail:
          "Most beachfront restaurants serve a proper midday paella on Sundays, order one for the whole table and plan a slow afternoon.",
        url: "https://www.visitaltea.es/",
      },
      {
        name: "La Nucia tapas crawl",
        detail:
          "Walk from the Plaça Major through the back streets, small bars serve a tapa with every drink. Cash helps.",
        url: "https://www.lanucia.es/turismo",
      },
      {
        name: "Bodegas & local wine",
        detail:
          "The inland Marina Alta produces excellent Moscatel and red blends. Several small bodegas around Xaló run weekend tastings.",
        url: "https://www.bodegasxalo.com/",
      },
    ],
  },
  {
    id: "family",
    title: "With kids",
    body:
      "The villa's private pool covers most rainy-day-but-actually-sunny moments, but when you want to leave the gate:",
    items: [
      {
        name: "Mundomar (Benidorm)",
        detail:
          "Smaller, calmer marine park, dolphins, sea lions, exotic birds. Better for younger children than the bigger parks.",
        url: "https://www.mundomarbenidorm.com/",
      },
      {
        name: "Terra Natura",
        detail:
          "Zoo + water park combo on the edge of Benidorm. The water park is included with the zoo ticket in summer.",
        url: "https://terranatura.com/",
      },
      {
        name: "Karting Finestrat",
        detail:
          "Outdoor karting track with junior karts. 10 minutes from the villa.",
        url: "https://kartingfinestrat.com/",
      },
    ],
  },
  {
    id: "practical",
    title: "Practical tips",
    body:
      "A few things we tell every guest before they arrive:",
    items: [
      {
        name: "Rent a car",
        detail:
          "La Nucia is hilly and public transport is limited. Most guests pick a small car up at Alicante (ALC) airport, about 50 minutes away.",
      },
      {
        name: "Best months",
        detail:
          "May, June, September and early October give you swimming weather without the August heat. July-August is hot (32-35 °C) but the villa is fully shaded and air-conditioned.",
      },
      {
        name: "Supermarkets",
        detail:
          "Mercadona and Consum in La Nucia for the weekly shop; the Sunday market for fresh produce.",
      },
      {
        name: "Sun & wind",
        detail:
          "UV is strong even in spring. Hats and reef-safe sunscreen recommended, especially for the lighthouse and Peñón walks.",
      },
    ],
  },
];

function GuidePage() {
  return (
    <article className="mx-auto max-w-3xl px-6 py-12">
      <nav className="mb-6 text-sm text-muted-foreground">
        <Link to="/" className="hover:underline">
          Casa Mi Pajobe
        </Link>{" "}
        · Local guide
      </nav>

      <header className="mb-8">
        <p className="text-xs font-medium uppercase tracking-[0.25em] text-muted-foreground">
          La Nucia · Costa Blanca
        </p>
        <h1 className="mt-2 font-display text-3xl font-semibold sm:text-4xl">
          Things to do in La Nucia & on the Costa Blanca
        </h1>
        <p className="mt-4 text-base text-muted-foreground sm:text-lg">
          A short, opinionated guide written by the hosts at Casa Mi Pajobe.
          Beaches, hikes, day trips, food and family activities, all within
          an easy drive of the villa.
        </p>
      </header>

      <nav aria-label="On this page" className="mb-10 rounded-xl border bg-card p-4">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          On this page
        </h2>
        <ul className="grid gap-1 text-sm sm:grid-cols-2">
          {SECTIONS.map((s) => (
            <li key={s.id}>
              <a href={`#${s.id}`} className="text-primary hover:underline">
                {s.title}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="space-y-12">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-24">
            <h2 className="font-display text-2xl font-semibold">{s.title}</h2>
            <p className="mt-3 text-muted-foreground">{s.body}</p>
            {s.items && (
              <ul className="mt-5 space-y-4">
                {s.items.map((item) => {
                  const inner = (
                    <>
                      <h3 className="flex items-center gap-1.5 font-semibold">
                        {item.name}
                        {item.url && (
                          <ExternalLink
                            aria-hidden
                            className="h-3.5 w-3.5 text-muted-foreground"
                          />
                        )}
                      </h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {item.detail}
                      </p>
                    </>
                  );
                  return (
                    <li key={item.name}>
                      {item.url ? (
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block rounded-lg border bg-card p-4 transition hover:border-primary hover:bg-accent/40 hover:shadow-sm"
                        >
                          {inner}
                        </a>
                      ) : (
                        <div className="rounded-lg border bg-card p-4">{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        ))}
      </div>

      <aside className="mt-14 rounded-2xl border bg-primary/5 p-6 text-center">
        <h2 className="font-display text-xl font-semibold">
          Stay in the heart of it
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Casa Mi Pajobe is a private-pool villa in La Nucia, 4 bedrooms,
          large outdoor kitchen, sleeps up to 8. The whole guide above is a
          15-45 minute drive from the front gate.
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link
            to="/book"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Check availability
          </Link>
          <Link
            to="/$lang"
            params={{ lang: "en" }}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            See the villa
          </Link>
        </div>
      </aside>
    </article>
  );
}